import * as Global from "../global";

export const isSamePosition = (p1: Point2d, p2: Point2d): boolean => {
    return (
        Math.abs(p1.x - p2.x) <= Global.MAX_POSITION_DIFF &&
        Math.abs(p1.y - p2.y) <= Global.MAX_POSITION_DIFF
    );
};

function areCollinear(p1: Point2d, p2: Point2d, p3: Point2d, epsilon = 1e-9) {
    const area = (p2.x - p1.x) * (p3.y - p1.y) -
                 (p3.x - p1.x) * (p2.y - p1.y);
    return Math.abs(area) < epsilon;
}

export const orthogonalIntersection = (p1: Point2d, p2: Point2d, p3: Point2d): Point2d => {
    if (areCollinear(p1, p2, p3)) {
        return p3;
    }

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    // Line1: p1 + t * (dx, dy)
    // Line2: p3 + s * (-dy, dx)

    const a = dx;
    const b = dy;
    const c = p3.x - p1.x;
    const d = -dy;
    const e = dx;
    const f = p3.y - p1.y;

    const denominator = a * e - d * b;

    if (Math.abs(denominator) < 1e-9) {
        return p3; // fallback in case of numerical issues
    }

    const t = (c * e - f * d) / denominator;

    const intersectionX = p1.x + t * dx;
    const intersectionY = p1.y + t * dy;

    return { x: intersectionX, y: intersectionY };
}

export const dot = (a: Point2d, b: Point2d) => {
    return a.x * b.x + a.y * b.y;
}

export const subtract = (a: Point2d, b: Point2d): Point2d => {
    return { x: a.x - b.x, y: a.y - b.y };
}

export const add = (a: Point2d, b: Point2d): Point2d => {
    return { x: a.x + b.x, y: a.y + b.y };
}

export const scale = (v: Point2d, s: number): Point2d => {
    return { x: v.x * s, y: v.y * s };
}

export const normalize = (v: Point2d): Point2d => {
    const len = Math.hypot(v.x, v.y);
    return len === 0 ? { x: 0, y: 0 } : { x: v.x / len, y: v.y / len };
}

export const length = (v: Point2d): number => {
  return Math.hypot(v.x, v.y);
}

export const angleBetween = (v1: Point2d, v2: Point2d): number => {
  return Math.atan2(v2.y, v2.x) - Math.atan2(v1.y, v1.x);
}

export const rotate = (v: Point2d, angle: number): Point2d => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return{
    x: v.x * cos - v.y * sin,
    y: v.x * sin + v.y * cos
  };
}

// export const scaleAlongLine = (p1: Point2d, p2: Point2d, scalar: number, point: Point2d): Point2d => {
//     const dir = normalize(subtract(p2, p1)); // unit direction vector

//     const v = subtract(point, p1); // vector from p1 to point
//     const projLength = dot(v, dir); // projection length on direction
//     const proj = scale(dir, projLength); // projected component
//     const ortho = subtract(v, proj); // orthogonal component

//     const scaledProj = scale(proj, scalar); // scale only along direction

//     const newVec = add(scaledProj, ortho); // combine scaled + unchanged
//     const newPt = add(p1, newVec); // translate back

//     return newPt;
// }

// export const transformBySegmentStretch = (p1: Point2d, p2: Point2d, p3: Point2d, point: Point2d) => {
//     const dir = normalize(subtract(p2, p1));
//     const d1 = subtract(p2, p1);
//     const len1 = Math.hypot(d1.x, d1.y);
//     const d2 = subtract(p3, p1);
//     const len2 = Math.hypot(d2.x, d2.y);
//     const scaleFactor = len2 / len1;

//     const v = subtract(point, p1);
//     const projLength = dot(v, dir);
//     const proj = scale(dir, projLength);
//     const ortho = subtract(v, proj);
//     const newVec = add(scale(proj, scaleFactor), ortho);
//     return add(p1, newVec);
// }


export const uniformScaleFromPoint = (origin: Point2d, targetBefore: Point2d, targetAfter: Point2d, points: Point2d[]): Point2d[] => {
  const v1 = subtract(targetBefore, origin);
  const v2 = subtract(targetAfter, origin);

  const len1 = length(v1);
  const len2 = length(v2);

  // Must be in same direction (dot > 0) to avoid flipping
  const dotProd = dot(v1, v2);
  if (dotProd <= 0) {
    throw new Error("Cannot scale uniformly without distortion (direction mismatch)");
  }

  const scaleFactor = len2 / len1;

  return points.map(pt => {
    const offset = subtract(pt, origin);
    const scaledOffset = scale(offset, scaleFactor);
    return add(origin, scaledOffset);
  });
}

export const transformByScalingAndRotation = (p1: Point2d, p2: Point2d, p3: Point2d, points: Point2d[]): Point2d[] => {
  const v1 = subtract(p2, p1);
  const v2 = subtract(p3, p1);

  const len1 = length(v1);
  const len2 = length(v2);
  const scaleFactor = len2 / len1;

  const angle = angleBetween(v1, v2);

  return points.map(pt => {
    const offset = subtract(pt, p1);
    const rotated = rotate(offset, angle);
    const scaled = scale(rotated, scaleFactor);
    return add(p1, scaled);
  });
}